import React, { useState, useRef } from 'react';
import axios from 'axios';
import PDFUploader from '../components/PDFUploader';
import MarkdownUploader from '../components/MarkdownUploader';
import TextractUploader from '../components/TextractUploader';
import DocumentList from '../components/DocumentList';
import './DocumentManagement.css';
import HighlightedTextarea from '../components/HighlightedTextarea';
import PromptStats from '../components/PromptStats';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function DocumentManagement() {
  const [refreshList, setRefreshList] = useState(0);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processMessage, setProcessMessage] = useState(null);
  const [showDefaultPrompt, setShowDefaultPrompt] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);
  const [filterInfo, setFilterInfo] = useState({ hasActiveFilters: false, totalCount: 0 });
  const [loadingFilteredIds, setLoadingFilteredIds] = useState(false);
  
  // Prompt variables state
  const [showPromptVariables, setShowPromptVariables] = useState(false);
  const [promptVariables, setPromptVariables] = useState(null);
  const [loadingPromptVariables, setLoadingPromptVariables] = useState(false);
  
  // LLM Provider state
  const [llmProvider, setLlmProvider] = useState('gemini');
  
  // Use ref to store fetchAllIds function to avoid stale closure issues
  const fetchAllIdsRef = useRef(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [cancelling, setCancelling] = useState(false);
  
  // Use ref for cancel flag so it persists across renders without causing re-renders
  const cancelRequestedRef = useRef(false);
  // AbortController ref to cancel in-flight requests
  const abortControllerRef = useRef(null);

  const handleUploadSuccess = () => {
    // Trigger list refresh by incrementing refreshList
    setRefreshList(prev => prev + 1);
  };

  const handleCancelProcessing = () => {
    cancelRequestedRef.current = true;
    setCancelling(true);
    // Abort the current in-flight request immediately
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleProcessAllDocuments = async () => {
    try {
      setProcessing(true);
      setProcessMessage(null);
      cancelRequestedRef.current = false; // Reset cancel flag
      setCancelling(false); // Reset cancelling state
      
      // If filters are active, fetch ALL matching document IDs first
      let documentIdsToProcess = null;
      
      console.log('Processing - filterInfo:', filterInfo, 'fetchAllIdsRef:', fetchAllIdsRef.current);
      
      if (filterInfo.hasActiveFilters && fetchAllIdsRef.current) {
        setProcessMessage({ type: 'info', text: 'Fetching all filtered documents...' });
        setLoadingFilteredIds(true);
        try {
          documentIdsToProcess = await fetchAllIdsRef.current();
          console.log('Fetched document IDs:', documentIdsToProcess?.length);
        } catch (err) {
          console.error('Error fetching filtered IDs:', err);
          setProcessMessage({ type: 'error', text: 'Failed to fetch filtered documents' });
          setProcessing(false);
          setLoadingFilteredIds(false);
          return;
        }
        setLoadingFilteredIds(false);
        setProcessMessage(null);
      } else if (filterInfo.hasActiveFilters && !fetchAllIdsRef.current) {
        console.error('Filters active but fetchAllIds function not available');
        setProcessMessage({ type: 'error', text: 'Unable to fetch filtered documents. Please refresh the page.' });
        setProcessing(false);
        return;
      }
      
      // If filtered documents are selected, process them one by one to show progress
      if (documentIdsToProcess && documentIdsToProcess.length > 0) {
        const total = documentIdsToProcess.length;
        let processedCount = 0;
        let skippedCount = 0;
        let errorCount = 0;
        let cancelled = false;
        
        setProgress({ current: 0, total, percentage: 0 });
        
        for (let i = 0; i < documentIdsToProcess.length; i++) {
          // Check if cancellation was requested before starting
          if (cancelRequestedRef.current) {
            cancelled = true;
            break;
          }
          
          const docId = documentIdsToProcess[i];
          const current = i + 1;
          const percentage = Math.round((current / total) * 100);
          
          setProgress({ current, total, percentage });
          
          // Create a new AbortController for this request
          abortControllerRef.current = new AbortController();
          
          try {
            const response = await axios.post(
              `${API_BASE_URL}/guidelines/process-document/${docId}`,
              {
                prompt: customPrompt || null,
                llm_provider: llmProvider
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                },
                timeout: 1800000, // 30 minute timeout per document
                signal: abortControllerRef.current.signal // Pass abort signal
              }
            );
            
            if (response.data && response.data.guideline_id) {
              processedCount++;
            } else {
              skippedCount++;
            }
          } catch (err) {
            // Check if this was an abort/cancel
            if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
              console.log(`Request for document ${docId} was cancelled`);
              cancelled = true;
              break;
            }
            console.error(`Error processing document ${docId}:`, err);
            errorCount++;
          }
        }
        
        // Clear the abort controller
        abortControllerRef.current = null;
        
        // Reset cancelling state
        setCancelling(false);
        
        if (cancelled) {
          setProcessMessage({ 
            type: 'warning', 
            text: `Operation cancelled. Processed: ${processedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}, Remaining: ${total - processedCount - skippedCount - errorCount}` 
          });
          // Keep dialog open when cancelled so user can see the result
          // Refresh document list to show any changes made before cancellation
          setRefreshList(prev => prev + 1);
          setProcessing(false);
          return; // Exit early, don't close dialog
        } else {
          setProgress({ current: total, total, percentage: 100 });
          
          setProcessMessage({ 
            type: 'success', 
            text: `Metadata generation complete! Processed: ${processedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}` 
          });
        }
      } else {
        // Process all documents using the batch endpoint (no progress bar)
        const response = await axios.post(
          `${API_BASE_URL}/guidelines/process-all-documents`,
          {
            prompt: customPrompt || null,
            document_ids: null,
            llm_provider: llmProvider
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 1800000 // 30 minute timeout for processing all documents
          }
        );

        console.log('All documents processed:', response.data);
        
        setProcessMessage({ 
          type: 'success', 
          text: `Metadata generation complete! Processed: ${response.data.processed_count || 0}, Skipped: ${response.data.skipped_count || 0}, Errors: ${response.data.error_count || 0}` 
        });
      }
      
      // Close dialog and clear prompt
      setShowPromptDialog(false);
      setCustomPrompt('');
      
      // Refresh document list to show updated metadata status
      setRefreshList(prev => prev + 1);
      
      setTimeout(() => {
        setProcessMessage(null);
        setProgress({ current: 0, total: 0, percentage: 0 });
      }, 10000);
    } catch (err) {
      console.error('Error processing documents:', err);
      let errorMessage = 'Failed to process documents with LLM';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Processing documents is taking longer than expected. Please try again or check the server logs.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setProcessMessage({ 
        type: 'error', 
        text: errorMessage
      });
      
      setProgress({ current: 0, total: 0, percentage: 0 });
    } finally {
      setProcessing(false);
      setProgress({ current: 0, total: 0, percentage: 0 }); // Reset progress
    }
  };

  return (
    <div className="document-management-page">
      {processMessage && (
        <div className={`process-message ${processMessage.type}`}>
          <span className="message-icon">
            {processMessage.type === 'success' ? '✅' : processMessage.type === 'warning' ? '⚠️' : '❌'}
          </span>
          <span>{processMessage.text}</span>
        </div>
      )}

      <TextractUploader onUploadSuccess={handleUploadSuccess} />
      <PDFUploader onUploadSuccess={handleUploadSuccess} />
      <MarkdownUploader onUploadSuccess={handleUploadSuccess} />
      <DocumentList 
        refreshTrigger={refreshList}
        onGenerateMetadataClick={() => setShowPromptDialog(true)}
        onFilteredDocumentsChange={(info) => {
          console.log('Filter info updated:', info);
          setFilterInfo({ hasActiveFilters: info.hasActiveFilters, totalCount: info.totalCount });
          // Store fetchAllIds in ref for reliable access
          fetchAllIdsRef.current = info.fetchAllIds;
        }}
      />

      {showPromptDialog && (
        <div 
          className="delete-confirm-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !processing) {
              setShowPromptDialog(false);
              setCustomPrompt('');
              setProcessMessage(null);
            }
          }}
        >
          <div 
            className="prompt-dialog" 
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Generate Metadata for {filterInfo.hasActiveFilters ? `Filtered Documents (${filterInfo.totalCount})` : 'All Documents'}</h3>
            <p>Enter a custom prompt to extract guideline metadata from {filterInfo.hasActiveFilters ? 'the filtered documents' : 'all documents'}. Leave empty to use the default prompt.</p>
            {filterInfo.hasActiveFilters ? (
              <p className="info-text">ℹ️ This will process {filterInfo.totalCount} filtered document(s).</p>
            ) : (
              <p className="warning-text">⚠️ This will process all documents in the database. This may take a long time.</p>
            )}
            
            {/* LLM Provider Selection */}
            <div style={{ 
              marginBottom: '1rem', 
              padding: '1rem', 
              background: '#f5f5f5', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <label htmlFor="llm-provider" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                LLM Provider:
              </label>
              <select
                id="llm-provider"
                value={llmProvider}
                onChange={(e) => setLlmProvider(e.target.value)}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  background: processing ? '#e5e5e5' : '#fff',
                  cursor: processing ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="openai">OpenAI GPT-5</option>
                <option value="gemini">Google Gemini 3.0</option>
              </select>
            </div>
            
            {/* Default Prompt Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!showDefaultPrompt && !defaultPrompt) {
                    setLoadingDefaultPrompt(true);
                    try {
                      const response = await axios.get(`${API_BASE_URL}/guidelines/default-prompt`);
                      setDefaultPrompt(response.data);
                    } catch (err) {
                      console.error('Error loading default prompt:', err);
                    } finally {
                      setLoadingDefaultPrompt(false);
                    }
                  }
                  setShowDefaultPrompt(!showDefaultPrompt);
                }}
                className="default-prompt-toggle"
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#f0f0f0', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showDefaultPrompt ? '▼' : '▶'} {loadingDefaultPrompt ? 'Loading...' : 'Show Default Prompt'}
              </button>
              {showDefaultPrompt && defaultPrompt && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '1rem',
                  background: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem'
                }}>
                  {defaultPrompt}
                </div>
              )}
              {showDefaultPrompt && defaultPrompt && (
                <PromptStats
                  text={defaultPrompt}
                  knownVariables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
                  label="Default Prompt"
                  estimateEndpoint="/guidelines/estimate-prompt-tokens"
                />
              )}
            </div>

            {/* Prompt Variables Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!showPromptVariables && !promptVariables) {
                    setLoadingPromptVariables(true);
                    try {
                      const response = await axios.get(`${API_BASE_URL}/guidelines/prompt-variables`);
                      setPromptVariables(response.data);
                    } catch (err) {
                      console.error('Error loading prompt variables:', err);
                    } finally {
                      setLoadingPromptVariables(false);
                    }
                  }
                  setShowPromptVariables(!showPromptVariables);
                }}
                className="default-prompt-toggle"
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#e8f4f8', 
                  border: '1px solid #b8d4e8', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                {showPromptVariables ? '▼' : '▶'} {loadingPromptVariables ? 'Loading Variables...' : 'Show Prompt Variables'}
              </button>
              {showPromptVariables && promptVariables && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '1rem', 
                  background: '#f0f8ff', 
                  border: '1px solid #b8d4e8', 
                  borderRadius: '4px'
                }}>
                  <p style={{ marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
                    These variables will be substituted in the prompt before sending to the LLM:
                  </p>
                  
                  {/* Insurers */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <code style={{ background: '#fff', padding: '0.2rem 0.5rem', borderRadius: '3px', fontWeight: 'bold' }}>{'{insurers}'}</code>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        ({promptVariables.insurers_count} insurers)
                      </span>
                    </div>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflow: 'auto', 
                      background: '#fff', 
                      padding: '0.5rem', 
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem'
                    }}>
                      {promptVariables.insurers.map((insurer, idx) => (
                        <div key={idx} style={{ padding: '0.2rem 0' }}>• {insurer}</div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Available Guideline IDs */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <code style={{ background: '#fff', padding: '0.2rem 0.5rem', borderRadius: '3px', fontWeight: 'bold' }}>{'{available_referenced_guideline_ids}'}</code>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        ({promptVariables.available_guideline_ids_count} guideline IDs)
                      </span>
                    </div>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflow: 'auto', 
                      background: '#fff', 
                      padding: '0.5rem', 
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem'
                    }}>
                      {promptVariables.available_guideline_ids.slice(0, 100).map((id, idx) => (
                        <div key={idx} style={{ padding: '0.2rem 0' }}>• {id}</div>
                      ))}
                      {promptVariables.available_guideline_ids.length > 100 && (
                        <div style={{ padding: '0.2rem 0', color: '#888', fontStyle: 'italic' }}>
                          ... and {promptVariables.available_guideline_ids.length - 100} more
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                    <strong>Note:</strong> The markdown content is automatically appended to the prompt.
                  </div>
                </div>
              )}
            </div>

            <HighlightedTextarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter custom prompt here, or leave empty to use default prompt..."
              disabled={processing}
              variables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
            />
            {customPrompt && (
              <PromptStats
                text={customPrompt}
                knownVariables={['insurers', 'available_referenced_guideline_ids', 'markdown_here']}
                label="Custom Prompt"
                estimateEndpoint="/guidelines/estimate-prompt-tokens"
              />
            )}

            {/* Progress Bar - Only show when processing filtered documents */}
            {processing && progress.total > 0 && (
              <div className="progress-container" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <div className="progress-header">
                  <span className={`progress-text ${cancelling ? 'cancelling-text' : ''}`}>
                    {cancelling 
                      ? `⚠️ Cancelling request...` 
                      : `Processing document ${progress.current} of ${progress.total} (${progress.percentage}%)`
                    }
                  </span>
                </div>
                <div className={`progress-bar-wrapper ${cancelling ? 'cancelling' : ''}`}>
                  <div 
                    className={`progress-bar-fill ${cancelling ? 'cancelling' : ''}`}
                    style={{ width: `${progress.percentage}%` }}
                  ></div>
                </div>
              </div>
            )}
            <div className="delete-confirm-buttons">
              {processing && progress.total > 0 ? (
                <button
                  onClick={handleCancelProcessing}
                  className={`cancel-processing-button ${cancelling ? 'cancelling' : ''}`}
                  disabled={cancelling}
                >
                  {cancelling ? '⏳ Cancelling...' : '⏹ Cancel Operation'}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleProcessAllDocuments}
                    disabled={processing}
                    className="confirm-delete-button"
                  >
                    {processing ? (loadingFilteredIds ? 'Loading...' : 'Processing...') : filterInfo.hasActiveFilters ? `Generate Metadata for ${filterInfo.totalCount} Filtered Document(s)` : 'Generate Metadata for All'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPromptDialog(false);
                      setCustomPrompt('');
                      setProcessMessage(null);
                    }}
                    disabled={processing}
                    className="cancel-delete-button"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManagement;

